import { useEffect, useState } from 'react';
import { ApiError, type Api, type SiteMessage, type SiteSessionRow } from '../api';
import type { T } from '../i18n';
import { when } from './Calls';
import { ScoreBadge } from './Leads';

/** A page address shown short: its path (the site name is shown separately). */
const pathOf = (url: string | null | undefined): string => {
  if (!url) return '';
  try {
    const u = new URL(url);
    return u.pathname + u.search || '/';
  } catch {
    return url;
  }
};

/**
 * ADR-076: website (text) conversations — the transcript with the page each message was written on, what the agent
 * collected (its own fact keys, the business named them) and a topic passed to the team.
 */
export const WebChats = ({ api, t, onOpenCall }: { api: Api; t: T; onOpenCall?: (callId: string) => void }) => {
  const [rows, setRows] = useState<SiteSessionRow[]>();
  const [error, setError] = useState<string>();
  const [selected, setSelected] = useState<string>();
  useEffect(() => {
    api.siteSessions().then((r) => setRows(r.items)).catch((e: unknown) => setError(e instanceof ApiError ? e.code : 'generic'));
  }, [api]);

  if (error) return <div className="banner error">{t(`error.${error}`) === `error.${error}` ? `${t('error.generic')} (${error})` : t(`error.${error}`)}</div>;
  if (!rows) return <div className="muted">{t('boot.loading')}</div>;
  if (rows.length === 0) return <div className="empty"><p>{t('web.empty')}</p></div>;
  const current = selected && rows.some((r) => r.id === selected) ? selected : rows[0]!.id;
  return (
    <div className="split">
      <div className="split-list">
        <div className="call-items">
          {rows.map((r) => (
            <button key={r.id} type="button" className={`call-item ${current === r.id ? 'active' : ''}`} onClick={() => setSelected(r.id)}>
              <span className="row-between">
                <span className="dir">{r.siteName}</span>
                <span className="row">
                  {r.analysis?.score && <ScoreBadge score={r.analysis.score} t={t} />}
                  {r.request && <span className="pill warn">{t('web.request')}</span>}
                  <span className={`pill ${r.endedAt ? 'muted' : 'published'}`}>{t(r.endedAt ? 'web.ended' : 'web.open')}</span>
                </span>
              </span>
              <bdi>{r.lastVisitorText ?? ''}</bdi>
              <span className="hint">{when(r.startedAt)} · {t('web.messages', { n: r.messages })}{r.lastPage ? ` · ${pathOf(r.lastPage.url)}` : ''}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="split-detail"><WebChatCard key={current} api={api} t={t} id={current} {...(onOpenCall ? { onOpenCall } : {})} /></div>
    </div>
  );
};

const WebChatCard = ({ api, t, id, onOpenCall }: { api: Api; t: T; id: string; onOpenCall?: (callId: string) => void }) => {
  const [d, setD] = useState<SiteSessionRow & { transcript: SiteMessage[] }>();
  const [error, setError] = useState<string>();
  useEffect(() => {
    api.siteSession(id).then(setD).catch((e: unknown) => setError(e instanceof ApiError ? e.code : 'generic'));
  }, [api, id]);
  if (error) return <div className="banner error">{t('error.generic')} ({error})</div>;
  if (!d) return <div className="muted">{t('boot.loading')}</div>;
  const facts = Object.entries(d.facts).filter(([, v]) => v !== undefined && v !== null && v !== '');
  let lastPage = '';
  return (
    <div className="card">
      <div className="kicker">{t('web.site')}: {d.siteName}</div>
      <h3>{when(d.startedAt)}</h3>
      {/* ADR-076: the conversation's report (the same analysis as a call), ready once the chat has ended. */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="row-between">
          <strong>{t('web.report')}</strong>
          {d.analysis?.score && <ScoreBadge score={d.analysis.score} t={t} />}
        </div>
        {!d.callId && <p className="muted small">{t('web.report.none')}</p>}
        {d.callId && !d.analysis && <p className="muted small">{t(d.endedAt ? 'web.report.pending' : 'web.report.afterEnd')}</p>}
        {d.analysis?.summary && <p>{d.analysis.summary}</p>}
        {d.analysis?.nextAction && <p className="next-action"><span className="muted">{t('calls.nextAction')}</span> {d.analysis.nextAction}</p>}
        {d.callId && onOpenCall && <button type="button" className="link" onClick={() => onOpenCall(d.callId!)}>{t('web.openCall')}</button>}
      </div>
      <div className="turn">
        <dt>{t('web.collected')}</dt>
        <dd>{facts.length ? facts.map(([k, v]) => <div key={k}><span className="muted">{k}:</span> <bdi>{String(v)}</bdi></div>) : <span className="muted">{t('web.noFacts')}</span>}</dd>
        {d.request && (<><dt>{t('web.request')}</dt><dd>{d.request}</dd></>)}
        {d.lastPage && (<><dt>{t('web.page')}</dt><dd><a href={d.lastPage.url} target="_blank" rel="noopener noreferrer">{d.lastPage.title || pathOf(d.lastPage.url)}</a></dd></>)}
      </div>
      <div className="transcript" style={{ marginTop: 16 }}>
        {d.transcript.map((m) => {
          const page = pathOf(m.page);
          const moved = page && page !== lastPage;
          lastPage = page || lastPage;
          return (
            <div key={m.id} className="turn-pair">
              {moved && <div className="hint">— {page} —</div>}
              <div className={`bubble ${m.role === 'visitor' ? 'user' : 'assistant'}`} title={when(m.at)}>{m.text}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
